import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { flushSync } from "react-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import { useWorkoutSession } from "@/lib/states/workout/hooks";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { buildWorkoutSessionExercises, type SessionExerciseRow } from "@/lib/utils/workoutSession";

type PlanWorkout = {
  day?: number | null;
  id: string;
  name: string;
  description?: string | null;
  exercises: SessionExerciseRow[];
};

interface StartWorkoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName?: string;
}

export default function StartWorkoutDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
}: StartWorkoutDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const org = useOrg();
  const { startWorkoutSession, activeWorkoutData } = useWorkoutSession();
  const [isStarting, setIsStarting] = useState(false);

  const { data: planRows, isLoading: isPlansLoading } =
    trpc.training.listForClient.useQuery(
      { clientId },
      { enabled: open },
    );
  const { data: templates, isLoading: isTemplatesLoading } =
    trpc.workouts.list.useQuery(undefined, {
      enabled: Boolean(open && org?.id),
    });

  const plans = useMemo(() => {
    return (planRows ?? []).map((plan) => ({
      id: plan.id,
      name: plan.name,
      is_primary: plan.is_primary,
      workouts: plan.workouts as PlanWorkout[],
    }));
  }, [planRows]);

  const handleStartWorkout = async (
    workout: PlanWorkout,
    planName?: string
  ) => {
    setIsStarting(true);
    const exercises = buildWorkoutSessionExercises(workout.exercises ?? []);
    startWorkoutSession({
      clientId,
      clientName,
      workoutId: workout.id,
      name: workout.name,
      planName,
      description: workout.description,
      logNotes: "",
      exercises,
      startTime: new Date(),
      returnTo: location.pathname,
    });
    flushSync(() => {
      onOpenChange(false);
    });
    try {
      await navigate({ to: "/workout-session" });
    } catch {
      setIsStarting(false);
    }
  };

  const handleStartEmpty = async () => {
    setIsStarting(true);
    startWorkoutSession({
      clientId,
      clientName,
      name: "New Workout",
      logNotes: "",
      exercises: [],
      startTime: new Date(),
      returnTo: location.pathname,
    });
    flushSync(() => {
      onOpenChange(false);
    });
    try {
      await navigate({ to: "/workout-session" });
    } catch {
      setIsStarting(false);
    }
  };

  const isLoading = isPlansLoading || isTemplatesLoading || isStarting;

  if (!open && isStarting) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      analyticsName="start_workout_dialog"
    >
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Start workout</DialogTitle>
        </DialogHeader>
        {isLoading && (
          <div className="flex items-center justify-center py-10">
            <LoadingSpinner className="size-6" />
          </div>
        )}
        {!isLoading && (
          <div className="flex flex-col gap-6">
            {activeWorkoutData && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Active workout in progress</AlertTitle>
                <AlertDescription>
                  Starting a new workout will override the current session.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex items-center justify-between border rounded-lg p-4">
              <div>
                <h3 className="text-lg font-semibold">Start empty workout</h3>
                <p className="text-sm text-muted-foreground">
                  Build a workout from scratch for this session.
                </p>
              </div>
              <Button onClick={handleStartEmpty}>Start empty</Button>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Client plan workouts</h3>
              {plans.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No training plan workouts found for this client.
                </p>
              )}
              {plans.map((plan) => (
                <div key={plan.id} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{plan.name}</h4>
                    {plan.is_primary && (
                      <Badge variant="outline">Primary</Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    {plan.workouts?.map((workout) => (
                      <div
                        key={workout.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                      >
                        <div>
                          <div className="font-medium">{workout.name}</div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleStartWorkout(workout, plan.name)
                          }
                        >
                          Start
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Workout library</h3>
              {(!templates || templates.length === 0) && (
                <p className="text-sm text-muted-foreground">
                  No workout templates found in your library.
                </p>
              )}
              <div className="grid gap-3">
                {(templates ?? []).map((workout: any) => (
                  <div
                    key={workout.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
                  >
                    <div>
                      <div className="font-medium">{workout.name}</div>
                      {workout.description && (
                        <div className="text-xs text-muted-foreground">
                          {workout.description}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleStartWorkout(workout as PlanWorkout)
                      }
                    >
                      Start
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
